// Business/data schemas and types for video domain
import { z } from 'zod'
import { ArchivableSchema } from '../archive'
import { AuditPropsSchema } from '../common'
import { TinyUserSchema } from '../entity'
import { CompanyIdSchema, customIdSchema, isId, TeamIdSchema, UserIdSchema, type CompanyId, type TeamId } from '../ids'

export type WithVideoId = {
  videoId: VideoId
}

export const VideoIdSchema = customIdSchema('Video', '_')
export type VideoId = z.infer<typeof VideoIdSchema>
export const isVideoId = isId(VideoIdSchema)

export const toVideoId = (video: WithVideoId): VideoId => video.videoId

export type VideoVisibilityStatus = 'Draft' | 'Published' | 'Removed'

export type VideoVisibilty = CompanyId | TeamId

type TranscodeStatus = 'Processing' | 'Complete' | 'Error'

export type TagCount = {
  tag: string
  count: number
}

export type UnsavedVideo = z.infer<typeof UnsavedVideoSchema>

export const VideoVisibilityIdSchema = z.union([CompanyIdSchema, TeamIdSchema])
export type VideoVisibilityId = z.infer<typeof VideoVisibilityIdSchema>

export const UnsavedVideoSchema = z.object({
  visibilityId: VideoVisibilityIdSchema.optional(),
  externalId: z.string().optional(),
  description: z.string(),
  status: z.enum(['Draft', 'Published', 'Removed']),
  thumbnailTimestamp: z.number().optional(),
  hideFromRecommendations: z.boolean().optional(),
  expiresAt: z.number().optional(),
})

export type VideoData = z.infer<typeof VideoDataSchema>

export const VideoDataSchema = z.object({
  ...ArchivableSchema.shape,
  ...AuditPropsSchema.shape,
  ...UnsavedVideoSchema.shape,
  videoId: VideoIdSchema,
  ownerId: UserIdSchema,
  owner: TinyUserSchema,
  transcodeId: z.string().optional(),
  transcodeProgress: z.number().optional(),
  transcodeStatus: z.enum(['Processing', 'Complete', 'Error']).optional(),
  publishedAt: z.number().optional(),
  reviewedAt: z.number().optional(),
  tags: z.array(z.string()).describe('auto updated from the description'),
  viewCount: z.number().describe('total number of times viewed, not unique users'),
  likeCount: z.number(),
  bookmarkCount: z.number(),
  reportCount: z.number(),
  duration: z.number(),
  uploadUrl: z.string().optional(),
  playlistUrl: z.string(),
  thumbnailBlurhash: z.string().optional(),
  thumbnailUrl: z.string(),
})
